"use client";
import { useState, useEffect } from "react";

interface CountdownTimerProps {
  endTime: number | string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const CountdownTimer = ({
  endTime,
  className = "",
  size = "md",
}: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const parseEndTime = (value: number | string): number => {
      if (typeof value === "number") return value;
      // Tip `number | string` diyor ama API bu alanı null gönderebiliyor;
      // typeof null === "object" olduğu için sayı dalına da girmez ve
      // value.includes(...) burada TypeError fırlatırdı. Effect içindeki hata
      // en yakın error boundary'ye gider, yani tüm sayfayı düşürürdü.
      if (typeof value !== "string") return NaN;
      // ISO 8601 format (e.g. "2026-08-09T20:46:54.000000Z") — use directly
      if (value.includes("T") || value.endsWith("Z")) {
        return new Date(value).getTime();
      }
      // "DD-MM-YYYY HH:mm:ss" → ms
      const [datePart, timePart] = value.split(" ");
      const [day, month, year] = datePart.split("-");
      return new Date(`${year}-${month}-${day}T${timePart}`).getTime();
    };

    const endMs = parseEndTime(endTime);

    const updateTimer = () => {
      const distance = endMs - Date.now();

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  const formatTime = (time: number) => time.toString().padStart(2, "0");

  const sizeClasses = {
    sm: "w-5 h-5 text-xs rounded-[4px] font-medium",
    md: "w-6 h-6 sm:w-8 sm:h-8 text-xs sm:text-sm rounded-md font-bold",
    lg: "w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-md font-bold",
  };

  const separatorSize = {
    sm: "text-xs",
    md: "text-xs sm:text-base",
    lg: "text-sm sm:text-lg",
  };

  return (
    <div className={`flex items-center gap-0.5 sm:gap-1 ${className}`}>
      {timeLeft.days > 0 && (
        <>
          <div
            className={`${sizeClasses[size]} bg-[#1f1f1f] text-white flex items-center justify-center px-1`}
          >
            {timeLeft.days}g
          </div>
          <p className={`text-black font-bold ${separatorSize[size]}`}>:</p>
        </>
      )}
      <div
        className={`${sizeClasses[size]} bg-[#1f1f1f] text-white flex items-center justify-center`}
      >
        {formatTime(timeLeft.hours)}
      </div>
      <p className={`text-black font-bold ${separatorSize[size]}`}>:</p>
      <div
        className={`${sizeClasses[size]} bg-[#1f1f1f] text-white flex items-center justify-center`}
      >
        {formatTime(timeLeft.minutes)}
      </div>
      <p className={`text-black font-bold ${separatorSize[size]}`}>:</p>
      <div
        className={`${sizeClasses[size]} bg-[#1f1f1f] text-white flex items-center justify-center`}
      >
        {formatTime(timeLeft.seconds)}
      </div>
    </div>
  );
};

export default CountdownTimer;
